import { getSupabase } from '@/lib/supabase';
import { api } from '@/lib/apiClient';
import type { TemplateSnapshot } from '@/types/nutrition';

export interface NutritionRequest {
  id: string;
  assignment_id: string;
  client_id: string;
  coach_id: string;
  request_type: 'portion_change' | 'food_swap' | 'meal_addition' | 'general_feedback';
  client_message: string | null;
  current_snapshot: TemplateSnapshot;
  requested_snapshot: TemplateSnapshot;
  status: 'pending' | 'approved' | 'rejected';
  coach_response: string | null;
  created_at: string;
  reviewed_at: string | null;
  dismissed_at: string | null;
}

export interface CreateNutritionRequestInput {
  assignmentId: string;
  requestType: 'portion_change' | 'food_swap' | 'meal_addition' | 'general_feedback';
  requestedSnapshot: TemplateSnapshot;
  clientMessage?: string;
}

// Client submits request
export async function createNutritionRequest(input: CreateNutritionRequestInput): Promise<NutritionRequest> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  // Check authentication and assignment
  const {
    data: { user },
    error: authError,
  } = await sb.auth.getUser();
  if (authError || !user) {
    throw new Error('Not authenticated');
  }

  // Check if assignment exists and belongs to user
  const { data: assignment, error: assignmentError } = await sb
    .from('client_nutrition_assignments')
    .select('id, client_id, coach_id, title')
    .eq('id', input.assignmentId)
    .single();

  if (assignmentError) {
    throw new Error('Assignment not found');
  }

  // Temporary testing override - remove in production
  const isTestMode = (window as any).testAsClient === true;

  if (!isTestMode && assignment?.client_id !== user.id) {
    throw new Error('You can only create requests for your own assignments');
  }

  const { data, error } = await sb
    .from('client_nutrition_requests')
    .insert({
      assignment_id: input.assignmentId,
      request_type: input.requestType,
      requested_snapshot: input.requestedSnapshot,
      client_message: input.clientMessage || null,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  // Create a client event to notify the coach (directly in database)
  try {
    await sb.from('client_events').insert({
      client_id: data.client_id,
      coach_id: data.coach_id,
      client_program_id: '00000000-0000-0000-0000-000000000000', // Placeholder for non-program events
      type: 'nutrition_request',
      summary: `Nutrition change request: ${getRequestTypeLabel(input.requestType)}`,
      details: {
        request_id: data.id,
        request_type: input.requestType,
        client_message: input.clientMessage || null,
      },
      priority: 1, // medium priority
      actor_id: data.client_id, // client is the actor
    });
  } catch (_eventError) {
    // Don't fail the request if event creation fails
  }

  return data as NutritionRequest;
}

function getRequestTypeLabel(type: string): string {
  switch (type) {
    case 'portion_change':
      return 'Portion Changes';
    case 'food_swap':
      return 'Food Substitutions';
    case 'meal_addition':
      return 'Meal Additions';
    case 'general_feedback':
      return 'General Feedback';
    default:
      return type;
  }
}

// Coach sees pending requests
export async function listPendingRequestsForCoach(): Promise<NutritionRequest[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const {
    data: { user },
  } = await sb.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await sb
    .from('client_nutrition_requests')
    .select(
      `
      *,
      client_nutrition_assignments!inner(
        title,
        client_id,
        start_date
      )
    `,
    )
    .eq('coach_id', user.id)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as NutritionRequest[];
}

// Get all requests for a specific assignment
export async function getRequestsForAssignment(assignmentId: string): Promise<NutritionRequest[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb
    .from('client_nutrition_requests')
    .select('*')
    .eq('assignment_id', assignmentId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as NutritionRequest[];
}

// Get pending requests count for a client
export async function getPendingRequestsCountForClient(clientId: string): Promise<number> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { count, error } = await sb
    .from('client_nutrition_requests')
    .select('*', { count: 'exact', head: true })
    .eq('client_id', clientId)
    .eq('status', 'pending');

  if (error) throw error;
  return count || 0;
}

// Coach approves request
export async function approveNutritionRequest(requestId: string, coachResponse?: string): Promise<any> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb.rpc('approve_nutrition_request', {
    p_request_id: requestId,
    p_coach_response: coachResponse || null,
  });

  if (error) throw error;
  return data;
}

// Coach rejects request
export async function rejectNutritionRequest(requestId: string, coachResponse?: string): Promise<any> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb.rpc('reject_nutrition_request', {
    p_request_id: requestId,
    p_coach_response: coachResponse || null,
  });

  if (error) throw error;
  return data;
}

// Get request history for a client (direct DB access - for coach views)
export async function getRequestHistory(clientId: string): Promise<NutritionRequest[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb
    .from('client_nutrition_requests')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as NutritionRequest[];
}

// Get request history for authenticated client (via Worker for httpOnly cookie auth)
export async function getClientRequestHistory(): Promise<NutritionRequest[]> {
  try {
    const response = await fetch('/api/clients/nutrition-requests', {
      credentials: 'include',
    });

    if (!response.ok) {
      return [];
    }

    const { requests } = await response.json();
    return requests || [];
  } catch (_error) {
    return [];
  }
}

// Dismiss a nutrition request notification (client-side)
export async function dismissNutritionRequest(requestId: string): Promise<void> {
  // The backend (functions/api/clients/nutrition-requests.ts) enforces
  // requireCsrf on PATCH; apiClient attaches X-CSRF-Token (fetching one if the cookie is missing).
  const res = await api.patch('/api/clients/nutrition-requests', { requestId });

  if (!res.ok) {
    const errorData = (res.body ?? {}) as { error?: string; message?: string };
    throw new Error(errorData.error || errorData.message || 'Failed to dismiss notification');
  }
}
